import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { breakevenService } from '../../services/financeService';
import { BreakevenAnalysis } from '../../types/finance';
import { FaSave, FaTrash, FaPlus, FaArrowLeft, FaBalanceScale } from 'react-icons/fa';
import toast from 'react-hot-toast';

const BreakEvenCalculator: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState<BreakevenAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState<BreakevenAnalysis>({
    name: '',
    product_name: '',
    fixed_costs: 0,
    variable_cost_per_unit: 0,
    selling_price_per_unit: 0,
    contribution_margin: 0,
    breakeven_units: 0,
    breakeven_revenue: 0,
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadAnalyses();
  }, [user, navigate]);

  const loadAnalyses = async () => {
    try {
      setLoading(true);
      const data = await breakevenService.getAll();
      setAnalyses(data);
    } catch (error) {
      console.error('Error loading breakeven analyses:', error);
      toast.error('Failed to load breakeven analyses');
    } finally {
      setLoading(false);
    }
  };

  const calculateBreakeven = (analysis: BreakevenAnalysis): { 
    contributionMargin: number; 
    breakevenUnits: number; 
    breakevenRevenue: number;
  } => {
    const { fixed_costs, variable_cost_per_unit, selling_price_per_unit } = analysis;
    
    // Contribution margin per unit = Selling price per unit - Variable cost per unit
    const contributionMargin = selling_price_per_unit - variable_cost_per_unit;
    
    // Break-even point in units = Fixed costs / Contribution margin per unit
    const breakevenUnits = contributionMargin > 0 
      ? fixed_costs / contributionMargin 
      : 0;
    
    // Break-even point in revenue = Break-even units × Selling price per unit
    const breakevenRevenue = breakevenUnits * selling_price_per_unit;
    
    return {
      contributionMargin: parseFloat(contributionMargin.toFixed(2)),
      breakevenUnits: Math.ceil(breakevenUnits), // Round up to ensure profitability
      breakevenRevenue: parseFloat(breakevenRevenue.toFixed(2))
    };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let parsedValue: string | number = value;
    
    // Parse numeric values
    if (['fixed_costs', 'variable_cost_per_unit', 'selling_price_per_unit'].includes(name)) {
      parsedValue = parseFloat(value) || 0;
    }
    
    setCurrentAnalysis(prev => ({
      ...prev,
      [name]: parsedValue
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { contributionMargin, breakevenUnits, breakevenRevenue } = calculateBreakeven(currentAnalysis);
      const analysisToSave = {
        ...currentAnalysis,
        user_id: user?.id,
        contribution_margin: contributionMargin,
        breakeven_units: breakevenUnits,
        breakeven_revenue: breakevenRevenue
      };
      
      if (currentAnalysis.id) {
        await breakevenService.update(currentAnalysis.id, analysisToSave);
        toast.success('Break-even analysis updated successfully');
      } else {
        await breakevenService.create(analysisToSave);
        toast.success('Break-even analysis created successfully');
      }
      
      setShowForm(false);
      setCurrentAnalysis({
        name: '',
        product_name: '',
        fixed_costs: 0,
        variable_cost_per_unit: 0,
        selling_price_per_unit: 0,
        contribution_margin: 0,
        breakeven_units: 0,
        breakeven_revenue: 0,
      });
      loadAnalyses();
    } catch (error) {
      console.error('Error saving break-even analysis:', error);
      toast.error('Failed to save break-even analysis');
    }
  };

  const handleEdit = (analysis: BreakevenAnalysis) => {
    setCurrentAnalysis(analysis);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this break-even analysis?')) {
      try {
        await breakevenService.delete(id);
        toast.success('Break-even analysis deleted successfully');
        loadAnalyses();
      } catch (error) {
        console.error('Error deleting break-even analysis:', error);
        toast.error('Failed to delete break-even analysis');
      }
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div className="relative container mx-auto px-4 py-8 overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-300 to-purple-400 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tr from-indigo-300 to-cyan-300 rounded-full filter blur-3xl opacity-20 translate-x-1/3 translate-y-1/3"></div>
      
      <div className="relative z-10 mb-6">
        <button
          onClick={() => navigate('/finance')}
          className="flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
        >
          <FaArrowLeft className="mr-2" /> Back to Financial Tools
        </button>
      </div>
      
      <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-8 mb-8">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-100/30 opacity-80"></div>
        
        <div className="relative z-10">
          <h1 className="text-3xl font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Break-Even Calculator</h1>
          <p className="text-gray-700 mb-8 text-lg">
            Find out exactly how many sales you need to cover your costs and start making profit.
          </p>
          
          <div className="relative overflow-hidden backdrop-blur-sm bg-blue-500/10 border border-blue-300/30 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-semibold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-indigo-700">How it works</h2>
            <p className="mb-3 text-gray-700">
              The break-even point is where your total revenue equals your total costs—the point at which you start making a profit.
            </p>
            <ul className="list-disc pl-6 mb-3 space-y-1.5">
              <li><strong className="text-blue-700">Fixed Costs</strong>: Expenses that don't change regardless of how many units you sell (rent, salaries, etc.)</li>
              <li><strong className="text-indigo-700">Variable Costs</strong>: Expenses that change based on production volume (materials, direct labor, etc.)</li>
              <li><strong className="text-purple-700">Contribution Margin</strong>: The amount each unit contributes to covering fixed costs and generating profit</li>
            </ul>
            <p className="text-gray-700">
              This calculator helps you determine how many units you need to sell to break even, and what revenue that represents.
            </p>
          </div>
        </div>
      </div>
      
      {!showForm ? (
        <div className="mb-8">
          <button
            onClick={() => {
              setCurrentAnalysis({
                name: '',
                product_name: '',
                fixed_costs: 0,
                variable_cost_per_unit: 0,
                selling_price_per_unit: 0,
                contribution_margin: 0,
                breakeven_units: 0,
                breakeven_revenue: 0,
              });
              setShowForm(true);
            }}
            className="relative overflow-hidden bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] flex items-center transition-all duration-200 group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            <span className="relative z-10 flex items-center">
              <FaPlus className="mr-2 group-hover:rotate-90 transition-transform duration-200" /> 
              <span>Create New Break-Even Analysis</span>
            </span>
          </button>
        </div>
      ) : (
        <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-8 mb-8">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 to-indigo-50/20 opacity-90"></div>
          
          <div className="relative z-10">
            <h2 className="text-xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              {currentAnalysis.id ? 'Edit Break-Even Analysis' : 'Create New Break-Even Analysis'}
            </h2>
          
            <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
              <div>
                <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">
                  Analysis Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={currentAnalysis.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                  placeholder="e.g., New Product Launch"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">
                  Product/Service Name
                </label>
                <input
                  type="text"
                  name="product_name"
                  value={currentAnalysis.product_name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                  placeholder="e.g., Handcrafted Candles"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
              <div>
                <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">
                  Fixed Costs ($)
                </label>
                <input
                  type="number"
                  name="fixed_costs"
                  value={currentAnalysis.fixed_costs}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                  placeholder="1000.00"
                  min="0"
                  step="0.01"
                  required
                />
                <span className="text-xs text-indigo-700 bg-indigo-50/50 backdrop-blur-sm px-3 py-1 rounded-md inline-block mt-2">
                  Total monthly costs that don't vary with production
                </span>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">
                  Variable Cost Per Unit ($)
                </label>
                <input
                  type="number"
                  name="variable_cost_per_unit"
                  value={currentAnalysis.variable_cost_per_unit}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                  placeholder="5.00"
                  min="0"
                  step="0.01"
                  required
                />
                <span className="text-xs text-indigo-700 bg-indigo-50/50 backdrop-blur-sm px-3 py-1 rounded-md inline-block mt-2">
                  Cost to produce one additional unit
                </span>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">
                  Selling Price Per Unit ($)
                </label>
                <input
                  type="number"
                  name="selling_price_per_unit"
                  value={currentAnalysis.selling_price_per_unit}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                  placeholder="15.00"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">
                Notes (Optional)
              </label>
              <textarea
                name="notes"
                value={currentAnalysis.notes || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                placeholder="Any additional information about this analysis"
                rows={3}
              />
            </div>
            
            <div className="flex flex-wrap justify-between items-center gap-4">
              <div className="flex flex-wrap gap-3">
                <button
                  type="submit"
                  className="relative overflow-hidden bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-5 py-2.5 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] flex items-center transition-all duration-200 group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                  <span className="relative z-10 flex items-center">
                    <FaSave className="mr-2 group-hover:scale-110 transition-transform duration-200" /> 
                    {currentAnalysis.id ? 'Update' : 'Save'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="bg-white/40 backdrop-blur-sm text-gray-700 border border-white/40 px-5 py-2.5 rounded-xl shadow-sm hover:bg-white/60 transition-all duration-200"
                >
                  Cancel
                </button>
              </div>
              
              {currentAnalysis.id && (
                <button
                  type="button"
                  onClick={() => handleDelete(currentAnalysis.id!)}
                  className="relative overflow-hidden bg-gradient-to-r from-red-500 to-pink-600 text-white px-5 py-2.5 rounded-xl shadow-lg hover:shadow-xl flex items-center transition-all duration-200 group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                  <span className="relative z-10 flex items-center">
                    <FaTrash className="mr-2 group-hover:scale-110 transition-transform duration-200" /> Delete
                  </span>
                </button>
              )}
            </div>
            
            {currentAnalysis.fixed_costs > 0 && 
             currentAnalysis.selling_price_per_unit > 0 && 
             currentAnalysis.variable_cost_per_unit < currentAnalysis.selling_price_per_unit && (
              <div className="mt-8 p-6 relative overflow-hidden backdrop-blur-sm bg-gradient-to-br from-green-50/40 to-emerald-50/40 border border-green-200/30 rounded-xl shadow-lg">
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-green-300/30 rounded-full filter blur-3xl"></div>
                <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-emerald-300/20 rounded-full filter blur-3xl"></div>
                
                <div className="relative z-10">
                  <h3 className="text-xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">Break-Even Analysis Results</h3>
                  
                  {(() => {
                    const { contributionMargin, breakevenUnits, breakevenRevenue } = calculateBreakeven(currentAnalysis);
                    
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <div className="relative overflow-hidden backdrop-blur-sm bg-white/40 p-5 rounded-xl shadow-sm border border-white/40 transition-all duration-200 hover:shadow-md hover:scale-[1.01] group">
                          <div className="absolute inset-0 bg-gradient-to-br from-green-50/20 to-emerald-50/20 opacity-80 group-hover:opacity-100 transition-opacity duration-200"></div>
                          <div className="relative z-10">
                            <p className="text-sm font-medium text-green-700 mb-2">Contribution Margin Per Unit</p>
                            <p className="text-2xl font-bold text-green-600">
                              ${contributionMargin.toFixed(2)}
                            </p>
                            <p className="text-xs text-green-700/80 mt-2 bg-green-50/50 backdrop-blur-sm px-2 py-1 rounded-lg inline-block">
                              Amount each unit contributes to covering fixed costs
                            </p>
                          </div>
                        </div>
                        
                        <div className="relative overflow-hidden backdrop-blur-sm bg-white/40 p-5 rounded-xl shadow-sm border border-white/40 transition-all duration-200 hover:shadow-md hover:scale-[1.01] group">
                          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 to-indigo-50/20 opacity-80 group-hover:opacity-100 transition-opacity duration-200"></div>
                          <div className="relative z-10">
                            <p className="text-sm font-medium text-blue-700 mb-2">Break-Even Point (Units)</p>
                            <p className="text-2xl font-bold text-blue-600">
                              {breakevenUnits.toLocaleString()} units
                            </p>
                            <p className="text-xs text-blue-700/80 mt-2 bg-blue-50/50 backdrop-blur-sm px-2 py-1 rounded-lg inline-block">
                              Number of units you need to sell to break even
                            </p>
                          </div>
                        </div>
                        
                        <div className="relative overflow-hidden backdrop-blur-sm bg-white/40 p-5 rounded-xl shadow-sm border border-white/40 transition-all duration-200 hover:shadow-md hover:scale-[1.01] group">
                          <div className="absolute inset-0 bg-gradient-to-br from-purple-50/20 to-indigo-50/20 opacity-80 group-hover:opacity-100 transition-opacity duration-200"></div>
                          <div className="relative z-10">
                            <p className="text-sm font-medium text-purple-700 mb-2">Break-Even Revenue</p>
                            <p className="text-2xl font-bold text-purple-600">
                              ${breakevenRevenue.toLocaleString()}
                            </p>
                            <p className="text-xs text-purple-700/80 mt-2 bg-purple-50/50 backdrop-blur-sm px-2 py-1 rounded-lg inline-block">
                              Total sales needed to cover all costs
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()} 
                </div>
                
                <div className="mt-8 pt-6 border-t border-green-200/30">
                  <h4 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-4">Profit/Loss Projection</h4>
                  
                  {(() => {
                    const { breakevenUnits } = calculateBreakeven(currentAnalysis);
                    const salesScenarios = [
                      { percent: 50, units: Math.floor(breakevenUnits * 0.5) },
                      { percent: 75, units: Math.floor(breakevenUnits * 0.75) },
                      { percent: 100, units: breakevenUnits },
                      { percent: 125, units: Math.ceil(breakevenUnits * 1.25) },
                      { percent: 150, units: Math.ceil(breakevenUnits * 1.5) },
                    ];
                    
                    return (
                      <div className="overflow-x-auto relative bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm">
                        <table className="min-w-full divide-y divide-blue-100/30">
                          <thead className="bg-gradient-to-r from-blue-50/50 to-indigo-50/50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-blue-700">Sales Level</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-blue-700">Units Sold</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-blue-700">Revenue</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-blue-700">Total Costs</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-blue-700">Profit/Loss</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-blue-100/20">
                            {salesScenarios.map((scenario) => {
                              const revenue = scenario.units * currentAnalysis.selling_price_per_unit;
                              const variableCosts = scenario.units * currentAnalysis.variable_cost_per_unit;
                              const totalCosts = currentAnalysis.fixed_costs + variableCosts;
                              const profit = revenue - totalCosts;
                              const isProfitable = profit >= 0;
                              
                              return (
                                <tr key={scenario.percent} className={`${scenario.percent === 100 ? 'bg-blue-50/30' : 'hover:bg-blue-50/20'} transition-colors duration-150`}>
                                  <td className="px-4 py-2.5 whitespace-nowrap font-medium">
                                    {scenario.percent}% of break-even
                                  </td>
                                  <td className="px-4 py-2.5 whitespace-nowrap">
                                    {scenario.units.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-2.5 whitespace-nowrap">
                                    ${revenue.toFixed(2)}
                                  </td>
                                  <td className="px-4 py-2.5 whitespace-nowrap">
                                    ${totalCosts.toFixed(2)}
                                  </td>
                                  <td className={`px-4 py-2.5 whitespace-nowrap font-medium ${isProfitable ? 'text-green-600' : 'text-red-600'}`}>
                                    <span className={`${isProfitable ? 'bg-green-50/60' : 'bg-red-50/60'} px-2 py-0.5 rounded-full`}>
                                      {isProfitable ? '+' : ''}{profit.toFixed(2)}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()} 
                </div>
              </div>
            )}
            
            {currentAnalysis.variable_cost_per_unit >= currentAnalysis.selling_price_per_unit && 
             currentAnalysis.selling_price_per_unit > 0 && (
              <div className="mt-8 p-6 relative overflow-hidden backdrop-blur-sm bg-gradient-to-br from-red-50/40 to-pink-50/40 border border-red-200/30 rounded-xl shadow-lg">
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-red-300/20 rounded-full filter blur-3xl"></div>
                <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-pink-300/20 rounded-full filter blur-3xl"></div>
                
                <div className="relative z-10">
                  <h3 className="text-xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-pink-600">Warning: Pricing Issue</h3>
                  <div className="bg-white/30 backdrop-blur-sm rounded-xl p-4 border border-red-100/30">
                    <p className="text-red-700">
                      Your variable cost per unit (<span className="font-semibold">${currentAnalysis.variable_cost_per_unit.toFixed(2)}</span>) is greater than or equal to 
                      your selling price (<span className="font-semibold">${currentAnalysis.selling_price_per_unit.toFixed(2)}</span>).
                    </p>
                    <p className="font-medium text-red-700 mt-2">
                      With this pricing, you will never break even regardless of how many units you sell.
                    </p>
                  </div>
                  
                  <div className="mt-4 bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-red-100/20">
                    <p className="text-red-700 font-medium flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Consider increasing your selling price or finding ways to reduce your variable costs.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </form>
          </div>
        </div>
      )}
      
      {analyses.length > 0 ? (
        <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-8 mb-8">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-100/30 opacity-80"></div>
          
          <div className="relative z-10">
            <h2 className="text-2xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Your Break-Even Analyses</h2>
            
            <div className="overflow-x-auto relative bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm">
              <table className="min-w-full divide-y divide-blue-100/30">
                <thead className="bg-gradient-to-r from-blue-50/70 to-indigo-50/70">
                  <tr>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                      Fixed Costs
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                      Selling Price
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                      Break-Even Units
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                      Break-Even Revenue
                    </th>
                    <th className="px-6 py-3.5 text-right text-xs font-semibold text-blue-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-100/20">
                  {analyses.map((analysis) => (
                    <tr key={analysis.id} className="hover:bg-blue-50/30 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-800">
                        {analysis.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        {analysis.product_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        <span className="bg-blue-50/50 px-2 py-0.5 rounded-md">
                          ${analysis.fixed_costs.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        <span className="bg-indigo-50/50 px-2 py-0.5 rounded-md">
                          ${analysis.selling_price_per_unit.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-blue-700">
                        <span className="bg-blue-50/50 px-2 py-0.5 rounded-md">
                          {analysis.breakeven_units.toLocaleString()} units
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-purple-700">
                        <span className="bg-purple-50/50 px-2 py-0.5 rounded-md">
                          ${analysis.breakeven_revenue.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(analysis)}
                          className="bg-indigo-50/50 text-blue-600 hover:text-blue-900 px-3 py-1 rounded-lg mr-2 transition-colors duration-200"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(analysis.id!)}
                          className="bg-red-50/50 text-red-600 hover:text-red-900 px-3 py-1 rounded-lg transition-colors duration-200"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        !showForm && (
          <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-10 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 opacity-70"></div>
            <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 w-40 h-40 bg-indigo-300/20 rounded-full filter blur-3xl"></div>
            <div className="absolute -bottom-20 left-1/2 transform -translate-x-1/2 w-40 h-40 bg-blue-300/20 rounded-full filter blur-3xl"></div>
            
            <div className="relative z-10">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 border border-white/40 flex items-center justify-center shadow-md">
                <FaBalanceScale className="text-blue-600 text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">No Analyses Yet</h3>
              <p className="text-gray-700 mb-6">You haven't created any break-even analyses yet.</p>
              <button
                onClick={() => setShowForm(true)}
                className="relative overflow-hidden bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200 group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                <span className="relative z-10">Create Your First Break-Even Analysis</span>
              </button>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default BreakEvenCalculator;
